/**
 * @fileoverview Pure helpers for automatic timesheet drafting (MOR-1041).
 * @module timesheets/activity/helpers
 *
 * No ctx, no side effects — everything here is unit-tested in isolation and
 * shared by the collectors, the drafting action and the review mutations:
 *
 * - local-day arithmetic in the practitioner's timezone (DST-safe);
 * - cross-source dedupe (the same email in Morgan and Outlook counts once);
 * - job attribution rules (explicit → candidate → unresolved, never forced);
 * - overlap detection between intervals;
 * - validation of the model's draft against real activity ids and
 *   authorised jobs/categories (source content can never widen this);
 * - reconciliation of a rerun against decisions already made.
 */

import type { Doc, Id } from "@cvx/_generated/dataModel";
import { sanitizePromptInput } from "@cvx/lib/ai/sanitize";
import type {
  ActivityKind,
  DurationBasis,
  JobMatch,
} from "@cvx/timesheets/activity/activityRecords/schema";
import type { SourceCoverage } from "@cvx/timesheets/activity/microsoftActivitySyncRuns/schema";

// ---------------------------------------------------------------------------
// Local days
// ---------------------------------------------------------------------------

export const DEFAULT_TIMEZONE = "Australia/Sydney";
const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True when `Intl` knows the zone; unknown zones fall back to the default. */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

function zonedParts(ms: number, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
}

/** Calendar date (YYYY-MM-DD) of an instant in a timezone. */
export function localDateIso(ms: number, timezone: string): string {
  try {
    const p = zonedParts(ms, timezone);
    return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

/** Wall-clock HH:MM of an instant in a timezone. */
export function localTimeHhMm(ms: number, timezone: string): string {
  try {
    const p = zonedParts(ms, timezone);
    return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
  } catch {
    return new Date(ms).toISOString().slice(11, 16);
  }
}

/** Local hour (0–23) of an instant in a timezone. */
export function localHour(ms: number, timezone: string): number {
  try {
    return zonedParts(ms, timezone).hour;
  } catch {
    return new Date(ms).getUTCHours();
  }
}

/** Offset (ms) such that `ms + offset` reads as the zone's wall clock in UTC. */
function zoneOffsetMs(ms: number, timezone: string): number {
  const p = zonedParts(ms, timezone);
  const asUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * The instant a wall-clock time (`HH:MM`) occurs on `localDate` in
 * `timezone`. The offset is resolved for that time of day, so the answer is
 * right on DST transition days too: on Sydney's spring-forward day 10:00
 * local is nine hours after local midnight, not ten. Never derive it as
 * "local midnight plus minutes" — that is wrong for every time after the
 * transition. (A wall time the transition skips resolves to the instant
 * one offset later, which is how calendars treat it.)
 */
export function localDateTimeMs(
  localDate: string,
  hhmm: string,
  timezone: string,
): number {
  if (!ISO_DATE.test(localDate)) throw new Error("Invalid local date");
  const [y, m, d] = localDate.split("-").map(Number);
  const [h, min] = hhmm.split(":").map(Number);
  if (![h, min].every((n) => Number.isFinite(n))) {
    throw new Error("Invalid local time");
  }
  const guess = Date.UTC(y, m - 1, d, h, min);
  try {
    const first = guess - zoneOffsetMs(guess, timezone);
    return guess - zoneOffsetMs(first, timezone);
  } catch {
    return guess;
  }
}

/** The instant local midnight starts on `localDate` in `timezone`. */
export function localMidnightMs(localDate: string, timezone: string): number {
  return localDateTimeMs(localDate, "00:00", timezone);
}

/** Add whole days to an ISO date (calendar arithmetic, timezone-free). */
export function addDaysIso(localDate: string, days: number): string {
  const [y, m, d] = localDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

/**
 * Epoch window covering the inclusive local-date range. `end` is exclusive
 * (the next day's midnight), so a 23- or 25-hour DST day is covered exactly.
 */
export function localRangeMs(
  startDate: string,
  endDate: string,
  timezone: string,
): { start: number; end: number } {
  return {
    start: localMidnightMs(startDate, timezone),
    end: localMidnightMs(addDaysIso(endDate, 1), timezone),
  };
}

/** Minutes between two HH:MM strings on one day, or undefined when unusable. */
export function minutesBetweenHhMm(
  start: string | undefined,
  end: string | undefined,
): number | undefined {
  if (!start || !end) return undefined;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => !Number.isFinite(n))) return undefined;
  const mins = eh * 60 + em - (sh * 60 + sm);
  return mins > 0 ? mins : undefined;
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/**
 * A short, single-line, injection-stripped summary. Activity text is
 * untrusted model input (an email subject can say "ignore your rules"), so
 * every summary passes through the prompt sanitiser and a hard length cap.
 */
export function clipSummary(text: string, max = 160): string {
  const clean = sanitizePromptInput(text.replace(/<[^>]*>/g, " "));
  return clean.length > max ? clean.slice(0, max - 1) + "…" : clean;
}

export function normaliseEmail(address: string): string {
  const match = /<([^>]+)>/.exec(address);
  return (match ? match[1] : address).trim().toLowerCase();
}

/** Lower-case alphanumerics of a title, for meeting reconciliation keys. */
export function titleSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .slice(0, 6)
    .join("-");
}

/**
 * The key a meeting shares whether it arrived as a Morgan key date or an
 * Outlook occurrence: same local day, same start, same (slugged) title.
 */
export function meetingDedupeKey(
  localDate: string,
  startHhMm: string | undefined,
  title: string,
): string {
  return `meeting:${localDate}:${startHhMm ?? "allday"}:${titleSlug(title)}`;
}

// ---------------------------------------------------------------------------
// Activity inputs
// ---------------------------------------------------------------------------

/** What a collector emits; the upsert adds identity, org/user and timing. */
export type ActivityInput = Omit<
  Doc<"activityRecords">,
  | "_id"
  | "_creationTime"
  | "orgId"
  | "userId"
  | "firstSeenAt"
  | "lastSeenAt"
  | "updatedAt"
  | "acceptedSuggestionId"
>;

export type ActivityRecordLike = Pick<
  Doc<"activityRecords">,
  | "_id"
  | "providerId"
  | "dedupeKey"
  | "jobId"
  | "jobMatch"
  | "candidateJobIds"
  | "kind"
  | "source"
  | "occurredAt"
  | "intervalStart"
  | "intervalEnd"
  | "durationBasis"
  | "durationMinutes"
  | "localDate"
  | "summary"
  | "sourceState"
  | "firstSeenAt"
>;

const MATCH_RANK: Record<JobMatch, number> = {
  explicit: 0,
  candidate: 1,
  unresolved: 2,
};

/**
 * Collapse records that describe the same activity (shared `dedupeKey`) into
 * one evidence item. The best-attributed record wins (explicit before
 * candidate before unresolved, then the one seen first); the rest ride
 * along as `duplicates` so evidence links to every source remain.
 */
export function dedupeRecords<T extends ActivityRecordLike>(
  records: T[],
): { primary: T; duplicates: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const r of records) {
    const key = r.dedupeKey ?? `provider:${r.providerId}`;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }
  return [...groups.values()].map((group) => {
    const sorted = [...group].sort(
      (a, b) =>
        MATCH_RANK[a.jobMatch] - MATCH_RANK[b.jobMatch] ||
        a.firstSeenAt - b.firstSeenAt,
    );
    return { primary: sorted[0], duplicates: sorted.slice(1) };
  });
}

/** Address → jobs it belongs to, built from the practitioner's job contacts. */
export type ContactIndex = ReadonlyMap<string, ReadonlySet<Id<"jobs">>>;

export const MAX_CANDIDATE_JOBS = 5;

/**
 * Attribute external activity to a job from its counterpart addresses.
 * Exactly one job across every address → explicit; two to five → candidates
 * for the practitioner to pick from; otherwise unresolved. Nothing here ever
 * picks a job on the practitioner's behalf.
 */
export function resolveJobMatch(
  participants: string[],
  contacts: ContactIndex,
  authorisedJobs: ReadonlySet<Id<"jobs">>,
): { jobMatch: JobMatch; jobId?: Id<"jobs">; candidateJobIds?: Id<"jobs">[] } {
  const jobs = new Set<Id<"jobs">>();
  for (const address of participants) {
    for (const jobId of contacts.get(normaliseEmail(address)) ?? []) {
      if (authorisedJobs.has(jobId)) jobs.add(jobId);
    }
  }
  const list = [...jobs];
  if (list.length === 1) return { jobMatch: "explicit", jobId: list[0] };
  if (list.length >= 2 && list.length <= MAX_CANDIDATE_JOBS) {
    return { jobMatch: "candidate", candidateJobIds: list };
  }
  return { jobMatch: "unresolved" };
}

/**
 * Words too generic to identify a matter by, so they never anchor a filename
 * match (entity suffixes, insolvency boilerplate, document-naming filler).
 */
const FILENAME_STOPWORDS: ReadonlySet<string> = new Set([
  "pty",
  "ltd",
  "limited",
  "inc",
  "llc",
  "corp",
  "company",
  "group",
  "holdings",
  "trust",
  "the",
  "and",
  "liquidation",
  "administration",
  "receivership",
  "insolvency",
  "draft",
  "final",
  "copy",
  "letter",
  "report",
  "document",
  "docx",
  "notice",
  "day",
  "one",
  "ref",
]);

/** A string's significant lowercase alphanumeric tokens (stopwords dropped). */
function significantTokens(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !FILENAME_STOPWORDS.has(t));
}

/** The document name inside a summary (`Edited document "X"`), or the whole text. */
export function documentNameFromSummary(summary: string): string {
  return /"([^"]+)"/.exec(summary)?.[1] ?? summary;
}

/**
 * Attribute a document to matters by its filename, as a fallback for files
 * edited outside a connected job folder (MOR-2340): the folder gave no job,
 * so we look for matters whose name or code the filename clearly contains.
 *
 * Matching is deliberately conservative — a matter's significant tokens are
 * joined into a compact needle (≥4 chars) that must appear in the compacted
 * filename, or its job code (≥3 chars) must. Matches are always returned as
 * *candidates* for the practitioner to confirm, never an explicit lock:
 * filenames are informal and a near-miss ("Smith" in "Smithfield") is better
 * surfaced for a human to reject than silently applied. More than
 * `MAX_CANDIDATE_JOBS` hits is treated as too ambiguous to suggest.
 */
export function matchJobsByFilename(
  filename: string,
  jobs: ReadonlyArray<{ jobId: Id<"jobs">; name: string; jobCode?: string }>,
): Id<"jobs">[] {
  const haystack = filename.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!haystack) return [];
  const matched = new Set<Id<"jobs">>();
  for (const job of jobs) {
    const nameNeedle = significantTokens(job.name).join("");
    const codeNeedle = (job.jobCode ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const nameHit = nameNeedle.length >= 4 && haystack.includes(nameNeedle);
    const codeHit = codeNeedle.length >= 3 && haystack.includes(codeNeedle);
    if (nameHit || codeHit) matched.add(job.jobId);
  }
  const list = [...matched];
  return list.length > 0 && list.length <= MAX_CANDIDATE_JOBS ? list : [];
}

// ---------------------------------------------------------------------------
// Overlaps
// ---------------------------------------------------------------------------

export type Interval = {
  key: string;
  start: number;
  end: number;
  label: string;
};

/** For each interval, the labels of the other intervals it overlaps. */
export function detectOverlaps(
  intervals: Interval[],
): Map<string, { key: string; label: string }[]> {
  const out = new Map<string, { key: string; label: string }[]>();
  for (const a of intervals) {
    const hits = intervals
      .filter((b) => b.key !== a.key && a.start < b.end && b.start < a.end)
      .map((b) => ({ key: b.key, label: b.label }));
    if (hits.length > 0) out.set(a.key, hits);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Job attribution for a group of evidence
// ---------------------------------------------------------------------------

export type AllowedJobs =
  | { kind: "conflict" }
  | { kind: "exact"; jobId: Id<"jobs"> }
  | { kind: "candidates"; jobIds: Id<"jobs">[] }
  | { kind: "none" };

/**
 * The jobs a proposal grounded in these records may name. Explicit evidence
 * decides; two different explicit jobs in one group is a conflict (the
 * grouping is wrong, not the evidence); with no explicit evidence the
 * candidates common to every candidate record remain; otherwise none.
 */
export function allowedJobsFor(records: ActivityRecordLike[]): AllowedJobs {
  const explicit = new Set(
    records.flatMap((r) =>
      r.jobMatch === "explicit" && r.jobId ? [r.jobId] : [],
    ),
  );
  if (explicit.size > 1) return { kind: "conflict" };
  if (explicit.size === 1) return { kind: "exact", jobId: [...explicit][0] };
  const candidateSets = records
    .filter((r) => r.jobMatch === "candidate")
    .map((r) => new Set(r.candidateJobIds ?? []));
  if (candidateSets.length === 0) return { kind: "none" };
  const common = [...candidateSets[0]].filter((id) =>
    candidateSets.every((set) => set.has(id)),
  );
  return common.length > 0
    ? { kind: "candidates", jobIds: common }
    : { kind: "none" };
}

// ---------------------------------------------------------------------------
// Duration basis and estimate policy
// ---------------------------------------------------------------------------

export type EstimatePolicy = {
  emailMinutes: number;
  noteMinutes: number;
  documentMinutes: number;
  /**
   * Minutes a session of report work (generation + review/editing) is assumed
   * to represent. Optional on stored policies written before this field
   * existed; `DEFAULT_REPORT_MINUTES` fills the gap, so no migration is needed.
   */
  reportMinutes?: number;
};

/** Fallback report allowance when a firm's stored policy predates the field. */
export const DEFAULT_REPORT_MINUTES = 30;

export const DEFAULT_ESTIMATE_POLICY: EstimatePolicy = {
  emailMinutes: 6,
  noteMinutes: 12,
  documentMinutes: 18,
  reportMinutes: DEFAULT_REPORT_MINUTES,
};

/** Policy minutes one record of a kind is assumed to represent. */
export function policyMinutesFor(
  kind: ActivityKind,
  policy: EstimatePolicy,
): number {
  switch (kind) {
    case "email_sent":
    case "mail_sent":
      return policy.emailMinutes;
    case "job_note":
      return policy.noteMinutes;
    case "document_edited":
      return policy.documentMinutes;
    case "report_activity":
      // In-app report generation and editing is real drafting work.
      return policy.reportMinutes ?? DEFAULT_REPORT_MINUTES;
    case "task_activity":
    case "data_entry_edited":
    case "agent_chat":
    case "mail_triaged":
    case "document_generated":
    case "document_uploaded":
    case "scheduled_event":
    case "calendar_event":
      // Corroborating proof-of-work: evidence the act happened, not how long
      // it took (MOR-2332 AC-2). Pressing Generate, uploading a file, editing
      // an agent table, chatting with an agent, triaging mail, and on-platform
      // tasks and key dates can all support an entry grounded in other
      // activity on the same job and day, but never carry time themselves.
      // (Microsoft calendar events get their booked minutes from
      // `durationMinutes` via `carriedMinutes`, not from this policy.)
      return 0;
  }
}

/**
 * How the proposal's duration should be labelled, from its evidence: a
 * measured interval wins; scheduled bookings are `scheduled` (and carry
 * the booked minutes so the review can show "booked, not confirmed");
 * anything else is an estimate.
 */
export function deriveDurationBasis(records: ActivityRecordLike[]): {
  basis: DurationBasis;
  scheduledMinutes?: number;
} {
  if (records.some((r) => r.durationBasis === "measured")) {
    return { basis: "measured" };
  }
  const scheduled = records.filter(
    (r) => r.durationBasis === "scheduled" && (r.durationMinutes ?? 0) > 0,
  );
  if (scheduled.length > 0 && scheduled.length === records.length) {
    return {
      basis: "scheduled",
      scheduledMinutes: scheduled.reduce(
        (sum, r) => sum + (r.durationMinutes ?? 0),
        0,
      ),
    };
  }
  if (scheduled.length > 0) {
    return {
      basis: "estimated",
      scheduledMinutes: scheduled.reduce(
        (sum, r) => sum + (r.durationMinutes ?? 0),
        0,
      ),
    };
  }
  return { basis: "estimated" };
}

/**
 * The most units an estimate grounded in these records may claim: twice the
 * policy allowance (plus any booked minutes), never below one unit. Keeps a
 * hallucinated "8 hours" from riding in on three emails.
 */
export function estimateCapUnits(
  records: ActivityRecordLike[],
  policy: EstimatePolicy,
  unitMinutes: number,
): number {
  const minutes = records.reduce(
    (sum, r) =>
      sum +
      (r.durationBasis === "scheduled" || r.durationBasis === "measured"
        ? (r.durationMinutes ?? 0)
        : policyMinutesFor(r.kind, policy)),
    0,
  );
  return Math.max(1, Math.ceil((minutes * 2) / Math.max(1, unitMinutes)));
}

type DurationEvidence = Pick<
  ActivityRecordLike,
  "kind" | "durationBasis" | "durationMinutes"
>;

function carriedMinutes(r: DurationEvidence, policy: EstimatePolicy): number {
  return r.durationBasis === "scheduled" || r.durationBasis === "measured"
    ? (r.durationMinutes ?? 0)
    : policyMinutesFor(r.kind, policy);
}

/**
 * Whether any of the evidence can support a duration at all: a booked or
 * measured interval, or a kind the firm's policy gives an allowance to.
 * Generated documents, uploads and untimed dates alone cannot.
 */
export function hasTimeBearingEvidence(
  records: DurationEvidence[],
  policy: EstimatePolicy,
): boolean {
  return records.some((r) => carriedMinutes(r, policy) > 0);
}

const DURATION_CLAIM =
  /\d+(\.\d+)?\s*-?\s*(min|mins|minute|minutes|hours?|hrs?|h|units?)\b/i;

/**
 * The model's one-line reason for a grouping, or nothing. A reason that
 * states a duration ("at 18 minutes per document") is discarded: durations
 * are explained by the server from validated figures (`describeDuration`),
 * never by free text the validator has not checked.
 */
export function groupingReason(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const reason = clipSummary(raw, 200);
  if (!reason || DURATION_CLAIM.test(reason)) return undefined;
  return reason;
}

function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The duration explanation shown on review, built only from validated
 * figures: the units on the suggestion, its basis, and the firm's estimate
 * policy applied to the evidence. Nothing here comes from model text.
 */
export function describeDuration(args: {
  units: number;
  unitMinutes: number;
  basis: DurationBasis;
  scheduledMinutes?: number;
  evidence: DurationEvidence[];
  policy: EstimatePolicy;
  edited: boolean;
}): string {
  const total = `${plural(args.units, "unit", "units")} (${minutesLabel(
    args.units * args.unitMinutes,
  )})`;
  if (args.edited) return `Set by you: ${total}.`;
  if (args.basis === "measured") return `Measured: ${total}.`;
  if (args.basis === "scheduled") {
    const booked =
      args.scheduledMinutes !== undefined
        ? `booked for ${minutesLabel(args.scheduledMinutes)} in the calendar, `
        : "";
    return `Scheduled: ${booked}proposed as ${total}. A booking does not confirm attendance.`;
  }

  const count = (kinds: ActivityKind[]) =>
    args.evidence.filter((e) => kinds.includes(e.kind)).length;
  const emails = count(["email_sent", "mail_sent"]);
  const notes = count(["job_note"]);
  const edits = count(["document_edited"]);
  const reports = count(["report_activity"]);
  const reportMinutes = args.policy.reportMinutes ?? DEFAULT_REPORT_MINUTES;
  const noAllowance = count(["document_generated", "document_uploaded"]);
  const booked = args.evidence.reduce(
    (sum, e) =>
      sum +
      (e.durationBasis === "scheduled" || e.durationBasis === "measured"
        ? (e.durationMinutes ?? 0)
        : 0),
    0,
  );
  const parts = [
    emails > 0
      ? `${plural(emails, "email", "emails")} × ${args.policy.emailMinutes} min`
      : "",
    notes > 0
      ? `${plural(notes, "file note", "file notes")} × ${args.policy.noteMinutes} min`
      : "",
    edits > 0
      ? `${plural(edits, "document edit", "document edits")} × ${args.policy.documentMinutes} min`
      : "",
    reports > 0
      ? `${plural(reports, "report session", "report sessions")} × ${reportMinutes} min`
      : "",
    booked > 0 ? `${minutesLabel(booked)} booked` : "",
  ].filter((p) => p !== "");
  const allowance =
    emails * args.policy.emailMinutes +
    notes * args.policy.noteMinutes +
    edits * args.policy.documentMinutes +
    reports * reportMinutes +
    booked;
  const documents =
    noAllowance > 0
      ? `${plural(noAllowance, "generated or uploaded document carries", "generated or uploaded documents carry")} no time allowance`
      : "";
  if (allowance === 0) {
    return `Estimated: ${total}. ${
      documents
        ? `${documents}, so`
        : "The evidence carries no time allowance, so"
    } this figure is not supported by the firm's drafting policy. Adjust it or dismiss the suggestion.`;
  }
  return `Estimated: ${total}. Firm allowance for this evidence: ${parts.join(
    " + ",
  )} = ${minutesLabel(allowance)}; estimates are capped at twice the allowance.${
    documents ? ` ${documents[0].toUpperCase()}${documents.slice(1)}.` : ""
  }`;
}

/** A booked interval: a Morgan key date with times or a calendar event. */
export function isMeeting(record: Pick<ActivityRecordLike, "kind">): boolean {
  return record.kind === "scheduled_event" || record.kind === "calendar_event";
}

/**
 * A time-bearing edit of one document. Each stands alone: distinct documents
 * are never grouped into a single entry (MOR-2340). Generated and uploaded
 * documents are corroborating-only and may still be folded into an entry.
 */
export function isDocumentEdit(
  record: Pick<ActivityRecordLike, "kind">,
): boolean {
  return record.kind === "document_edited";
}

/** Wording that claims the practitioner was there. */
const ASSERTS_ATTENDANCE =
  /\b(attend\w*|participat\w*|joined|took part|was present)\b/i;

/**
 * A neutral narrative for an entry grounded in a booking. A calendar entry
 * shows that a meeting was scheduled, not that the practitioner was at it
 * (MOR-2337 AC-3), so the wording names the meeting and leaves attendance
 * for the practitioner to confirm by accepting or adjusting the entry.
 */
export function meetingNarrative(
  meeting: Pick<ActivityRecordLike, "summary">,
  withOtherWork: boolean,
): string {
  const title = /"([^"]+)"/.exec(meeting.summary)?.[1] ?? "scheduled meeting";
  return `Meeting: ${title}${withOtherWork ? " and related work" : ""}`;
}

// ---------------------------------------------------------------------------
// Model output validation
// ---------------------------------------------------------------------------

export type DraftJob = {
  jobId: Id<"jobs">;
  isPreAppointment: boolean;
};

export type DraftCategory = {
  _id: Id<"timeCategories">;
  key: string;
  scope: "firm" | "matter";
};

export type ValidatedProposal = {
  date: string;
  jobId?: Id<"jobs">;
  jobMatch: JobMatch;
  candidateJobIds?: Id<"jobs">[];
  categoryId?: Id<"timeCategories">;
  narrative: string;
  units: number;
  billable: boolean;
  projectCode: "pre" | "post";
  confidence: "high" | "medium" | "low";
  reason?: string;
  activityIds: Id<"activityRecords">[];
  durationBasis: DurationBasis;
  scheduledMinutes?: number;
};

export type RejectedProposal = { reason: string };

type RawEntry = {
  date?: unknown;
  jobIndex?: unknown;
  categoryKey?: unknown;
  narrative?: unknown;
  units?: unknown;
  projectCode?: unknown;
  confidence?: unknown;
  reason?: unknown;
  activityIndexes?: unknown;
};

export type ValidationContext = {
  startDate: string;
  endDate: string;
  jobs: DraftJob[];
  categories: DraftCategory[];
  /** Evidence items in prompt order (index = the model's activityIndex). */
  evidence: ActivityRecordLike[][];
  roundUnits: (units: number) => number;
  maxUnits: number;
  unitMinutes: number;
  policy: EstimatePolicy;
};

/**
 * Turn the model's raw JSON into proposals the server vouches for. Every
 * check is deterministic and none of it can be relaxed by activity text:
 * unknown evidence indexes are dropped, an entry with no surviving evidence
 * is rejected, the job must be one the evidence allows (or none), the
 * category must be the firm's, dates must be in range, and estimated units
 * are capped by the firm's policy.
 */
export function validateDraftOutput(
  rawEntries: unknown,
  ctx: ValidationContext,
): { proposals: ValidatedProposal[]; rejected: RejectedProposal[] } {
  const proposals: ValidatedProposal[] = [];
  const rejected: RejectedProposal[] = [];
  const entries = Array.isArray(rawEntries) ? (rawEntries as RawEntry[]) : [];
  const categoriesByKey = new Map(ctx.categories.map((c) => [c.key, c]));
  const claimed = new Set<Id<"activityRecords">>();

  for (const raw of entries) {
    const date = typeof raw.date === "string" ? raw.date : "";
    if (!ISO_DATE.test(date) || date < ctx.startDate || date > ctx.endDate) {
      rejected.push({ reason: "date outside the drafted range" });
      continue;
    }
    const indexes = Array.isArray(raw.activityIndexes)
      ? raw.activityIndexes.filter(
          (i): i is number => typeof i === "number" && Number.isInteger(i),
        )
      : [];
    const groups = indexes
      .map((i) => ctx.evidence[i])
      .filter(
        (g): g is ActivityRecordLike[] => g !== undefined && g.length > 0,
      );
    // An evidence item is one activity, possibly seen in several sources.
    // Every record of it is linked to the proposal, but durations, caps and
    // the time-bearing check count the activity once (its primary record).
    const unclaimed = groups.filter((g) => !g.some((r) => claimed.has(r._id)));
    if (unclaimed.length === 0) {
      rejected.push({ reason: "no valid activity referenced" });
      continue;
    }
    // Some evidence stands alone and is never folded in with its own kind:
    //  - Two bookings are never one entry: overlapping ones would have their
    //    booked time added together unseen, and a private appointment would
    //    be folded into other work.
    //  - Two different documents are never one entry: each edited document is
    //    its own piece of work, so distinct files get distinct rows rather
    //    than a single lumped "document preparation" entry (MOR-2340).
    // When an entry cites several of either, those are all taken out of it;
    // the safety net then lists each on its own row, where overlaps between
    // rows are flagged.
    const meetings = unclaimed.filter((g) => isMeeting(g[0]));
    const docEdits = unclaimed.filter((g) => isDocumentEdit(g[0]));
    const standsAlone = (g: ActivityRecordLike[]): boolean =>
      (meetings.length > 1 && isMeeting(g[0])) ||
      (docEdits.length > 1 && isDocumentEdit(g[0]));
    const open = unclaimed.filter((g) => !standsAlone(g));
    const records = open.flat();
    const primaries = open.map((g) => g[0]);
    if (records.length === 0) {
      rejected.push({
        reason:
          docEdits.length > 1
            ? "several documents in one entry"
            : "several meetings in one entry",
      });
      continue;
    }
    const narrative =
      typeof raw.narrative === "string" ? clipSummary(raw.narrative, 600) : "";
    if (!narrative) {
      rejected.push({ reason: "empty narrative" });
      continue;
    }

    if (!hasTimeBearingEvidence(primaries, ctx.policy)) {
      // Generated documents, uploads and untimed dates alone do not show
      // time spent; proposing a duration for them would invent work.
      rejected.push({ reason: "evidence carries no time" });
      continue;
    }

    const allowed = allowedJobsFor(records);
    if (allowed.kind === "conflict") {
      rejected.push({ reason: "evidence spans two different jobs" });
      continue;
    }
    const jobIndex =
      typeof raw.jobIndex === "number" ? Math.trunc(raw.jobIndex) : -1;
    const requestedJob = ctx.jobs[jobIndex];
    const jobId: Id<"jobs"> | undefined = (() => {
      if (allowed.kind === "exact") return allowed.jobId;
      if (allowed.kind === "candidates") {
        return requestedJob && allowed.jobIds.includes(requestedJob.jobId)
          ? requestedJob.jobId
          : undefined;
      }
      return undefined;
    })();
    const jobMatch: JobMatch =
      jobId === undefined
        ? "unresolved"
        : records.every((r) => r.jobMatch === "explicit" && r.jobId === jobId)
          ? "explicit"
          : "candidate";
    const job = ctx.jobs.find((j) => j.jobId === jobId);

    const category =
      typeof raw.categoryKey === "string"
        ? categoriesByKey.get(raw.categoryKey)
        : undefined;

    const { basis, scheduledMinutes } = deriveDurationBasis(primaries);
    const requestedUnits = Number(raw.units);
    const rounded = ctx.roundUnits(
      Number.isFinite(requestedUnits) ? requestedUnits : 1,
    );
    const cap =
      basis === "measured"
        ? ctx.maxUnits
        : Math.min(
            ctx.maxUnits,
            estimateCapUnits(primaries, ctx.policy, ctx.unitMinutes),
          );
    const units = Math.max(1, Math.min(cap, rounded || 1));

    const confidence: "high" | "medium" | "low" =
      raw.confidence === "high" && jobMatch === "explicit"
        ? "high"
        : raw.confidence === "high" || raw.confidence === "medium"
          ? "medium"
          : "low";
    const projectCode: "pre" | "post" =
      raw.projectCode === "pre"
        ? "pre"
        : raw.projectCode === "post"
          ? "post"
          : job?.isPreAppointment
            ? "pre"
            : "post";

    // An entry grounded in a booking must not say the meeting was attended.
    const meeting = primaries.find(isMeeting);
    const wording =
      meeting && ASSERTS_ATTENDANCE.test(narrative)
        ? meetingNarrative(meeting, primaries.length > 1)
        : narrative;

    for (const r of records) claimed.add(r._id);
    proposals.push({
      date,
      jobId,
      jobMatch,
      candidateJobIds:
        allowed.kind === "candidates" ? allowed.jobIds : undefined,
      categoryId: category?._id,
      narrative: wording,
      units,
      // Billability is never the model's call (MOR-1041): firm-overhead
      // categories and pre-appointment matters are non-billable by firm
      // rule; everything else defaults to billable for the reviewer to flip.
      billable:
        category?.scope === "firm" || projectCode === "pre" ? false : true,
      projectCode,
      confidence,
      reason: groupingReason(raw.reason),
      activityIds: records.map((r) => r._id),
      durationBasis: basis,
      scheduledMinutes,
    });
  }
  return { proposals, rejected };
}

/** Evidence that is reviewed together when nothing else grouped it. */
function evidenceFamily(primary: ActivityRecordLike): string {
  switch (primary.kind) {
    case "email_sent":
    case "mail_sent":
      return "correspondence";
    case "job_note":
    case "task_activity":
      return "file work";
    case "document_edited":
      // Each edited document stands alone: distinct files are never grouped
      // into one entry, so the safety net lists them on separate rows too.
      return `document:${primary._id}`;
    case "document_generated":
    case "document_uploaded":
      return "documents";
    case "report_activity":
      return "reports";
    case "data_entry_edited":
      return "data work";
    case "agent_chat":
      return "agent chats";
    case "mail_triaged":
      return "mail";
    case "scheduled_event":
    case "calendar_event":
      // Each booking stands alone: two meetings are never one entry.
      return `meeting:${primary._id}`;
  }
}

export const UNGROUPED_REASON =
  "Listed for review: this activity was not grouped into another entry.";

/**
 * The safety net behind "unresolved activity is retained" (MOR-2333 AC-1).
 * Whatever the model does, time-bearing evidence it left out of every
 * entry still reaches the practitioner: grouped by day, job attribution and
 * kind, sized at the firm allowance (or the booked interval), marked low
 * confidence, with no job forced and no category chosen. Evidence that
 * carries no time (generated documents, uploads) is not listed on its own.
 */
export function coverUnclaimedEvidence(
  proposals: ValidatedProposal[],
  ctx: ValidationContext,
): ValidatedProposal[] {
  const claimed = new Set(proposals.flatMap((p) => p.activityIds));
  const buckets = new Map<string, ActivityRecordLike[][]>();
  for (const group of ctx.evidence) {
    const primary = group[0];
    if (primary === undefined) continue;
    if (group.some((r) => claimed.has(r._id))) continue;
    if (!hasTimeBearingEvidence([primary], ctx.policy)) continue;
    if (primary.localDate < ctx.startDate || primary.localDate > ctx.endDate) {
      continue;
    }
    const allowed = allowedJobsFor(group);
    const jobKey =
      allowed.kind === "exact"
        ? `job:${allowed.jobId}`
        : allowed.kind === "candidates"
          ? `candidates:${[...allowed.jobIds].sort().join(",")}`
          : "none";
    const key = `${primary.localDate}|${jobKey}|${evidenceFamily(primary)}`;
    buckets.set(key, [...(buckets.get(key) ?? []), group]);
  }

  return [...buckets.values()].map((groups) => {
    const records = groups.flat();
    const primaries = groups.map((g) => g[0]);
    const allowed = allowedJobsFor(records);
    const jobId = allowed.kind === "exact" ? allowed.jobId : undefined;
    const job = ctx.jobs.find((j) => j.jobId === jobId);
    const minutes = primaries.reduce(
      (sum, r) => sum + carriedMinutes(r, ctx.policy),
      0,
    );
    const { basis, scheduledMinutes } = deriveDurationBasis(primaries);
    return {
      date: primaries[0].localDate,
      jobId,
      jobMatch:
        jobId === undefined ? ("unresolved" as const) : ("explicit" as const),
      candidateJobIds:
        allowed.kind === "candidates" ? allowed.jobIds : undefined,
      categoryId: undefined,
      narrative: isMeeting(primaries[0])
        ? meetingNarrative(primaries[0], false)
        : clipSummary(primaries.map((r) => r.summary).join("; "), 300),
      units: Math.max(
        1,
        Math.min(
          ctx.maxUnits,
          ctx.roundUnits(minutes / Math.max(1, ctx.unitMinutes)) || 1,
        ),
      ),
      billable: job ? !job.isPreAppointment : true,
      projectCode: job?.isPreAppointment ? ("pre" as const) : ("post" as const),
      confidence: "low" as const,
      reason: UNGROUPED_REASON,
      activityIds: records.map((r) => r._id),
      durationBasis: basis,
      scheduledMinutes,
    };
  });
}

// ---------------------------------------------------------------------------
// Rerun reconciliation
// ---------------------------------------------------------------------------

export type ExistingSuggestion = {
  _id: Id<"timeEntrySuggestions">;
  status: "pending" | "accepted" | "dismissed" | "superseded";
  editedAt?: number;
  activityIds: Id<"activityRecords">[];
};

export type ReconcilePlan<P extends { activityIds: Id<"activityRecords">[] }> =
  {
    /** Proposals to insert, with evidence already decided on removed. */
    insert: P[];
    /** Unedited pending suggestions replaced by this run. */
    supersede: Id<"timeEntrySuggestions">[];
    /** Proposals dropped because every piece of evidence was already decided. */
    dropped: number;
  };

/**
 * Apply a rerun without undoing the practitioner: evidence behind accepted,
 * dismissed or edited suggestions is never re-proposed; unedited pending
 * suggestions from earlier runs are superseded (they were "re-drafted");
 * late evidence enriches nothing that is already decided — it becomes a
 * fresh, smaller proposal.
 */
export function reconcileProposals<
  P extends { activityIds: Id<"activityRecords">[] },
>(existing: ExistingSuggestion[], proposals: P[]): ReconcilePlan<P> {
  const decided = new Set<Id<"activityRecords">>();
  const replaceable: Id<"timeEntrySuggestions">[] = [];
  for (const s of existing) {
    const keep =
      s.status === "accepted" ||
      s.status === "dismissed" ||
      (s.status === "pending" && s.editedAt !== undefined);
    if (keep) {
      for (const id of s.activityIds) decided.add(id);
    } else if (s.status === "pending") {
      replaceable.push(s._id);
    }
  }
  const insert = proposals.flatMap((p) => {
    const activityIds = p.activityIds.filter((id) => !decided.has(id));
    return activityIds.length > 0 ? [{ ...p, activityIds }] : [];
  });
  return {
    insert,
    supersede: replaceable,
    dropped: proposals.length - insert.length,
  };
}

// ---------------------------------------------------------------------------
// Connection health
// ---------------------------------------------------------------------------

export type ConnectionHealth =
  | "connected"
  | "syncing"
  | "stale"
  | "consent_required"
  | "unavailable"
  | "disconnected";

/** A completed sync older than this is reported as stale. */
export const STALE_AFTER_MS = 36 * 60 * 60 * 1000;
/** A sync started this long ago without finishing is no longer "syncing". */
export const SYNC_ZOMBIE_MS = 20 * 60 * 1000;

export function connectionHealth(
  connection: Pick<
    Doc<"microsoftActivityConnections">,
    | "status"
    | "lastSyncStartedAt"
    | "lastSyncCompletedAt"
    | "lastErrorKind"
    | "consecutiveFailures"
  >,
  now: number,
): ConnectionHealth {
  if (connection.status === "disconnected") return "disconnected";
  if (connection.status === "consent_required") return "consent_required";
  const started = connection.lastSyncStartedAt ?? 0;
  const completed = connection.lastSyncCompletedAt ?? 0;
  if (started > completed && now - started < SYNC_ZOMBIE_MS) return "syncing";
  if (connection.consecutiveFailures >= 3) return "unavailable";
  if (completed === 0 || now - completed > STALE_AFTER_MS) return "stale";
  return "connected";
}

/** Merge per-source coverage from several passes (last word wins per source). */
export function mergeCoverage(...passes: SourceCoverage[][]): SourceCoverage[] {
  const bySource = new Map<SourceCoverage["source"], SourceCoverage>();
  for (const pass of passes) for (const c of pass) bySource.set(c.source, c);
  return [...bySource.values()];
}
